import { BCMSDate, BCMSCurrency } from "@bcm-dev/bcms-vite";
import { Account } from "model";

/**
 * @class TASPositivePayExceptionCheckForm
 * @classdesc Class definition for controlling a check
 * form which will then be transformed into a positive pay check.
 * 
 * @property {STRING} _id - The id of the check form.
 * @property {Account} _account - The account of the check form.
 * @property {BCMSDate} _issuedDate - The issued date of the check form.
 * @property {STRING} _checkNumber - The check number of the check form.
 * @property {BCMSCurrency} _amount - The amount of the check form.
 * @property {STRING} _payee - The payee of the check form.
 * @property {STRING} _referenceNumber - The reference number of the check form.
 * @property {STRING} _type - The type of the check form.
 * @property {STRING} _status - The current status of the check form.
 * @property {STRING} _action - The current action of the check form.
 * @property {STRING} _reason - The current reason of the check form.
 * @property {STRING} _returnReason - The current return reason of the check form.
 */
export default class TASPositivePayExceptionCheckForm {
    constructor(record) {
        this.id = record.id;
        this.account = record.account;
        this.issuedDate = record.issuedDate;
        this.checkNumber = record.checkNumber;
        this.amount = record.amount;
        this.payee = record.payee;
        this.referenceNumber = record.referenceNumber;
        this.type = record.type;
        this.status = record.status;
        this.action = record.action;
        this.reason = record.reason;
        this.returnReason = record.returnReason;
        this.didClickReturnReason = record.didClickReturnReason;
    }

    /**
     * @name id
     * @instance
     * @property set {STRING} - sets the id
     * @memberof TASPositivePayExceptionCheckForm
     */
    set id(value) { this._id = value; }

    /**
     * @name account
     * @instance
     * @property get {STRING} - returns the account.
     * @memberof TASPositivePayExceptionCheckForm
     */
    set account(val) {
        if(val) {
            if(val.id) {
                this._account = val;
            } else {
                this._account = Account.initByType(val);
            }
        } else {
            this._account = val;
        }
    }

    /**
     * @name issuedDate
     * @instance
     * @property set {STRING} - sets the issuedDate
     * @memberof TASPositivePayExceptionCheckForm
     */
    set issuedDate(value) {
        if(value instanceof BCMSDate) {
            this._issuedDate = value;
        } else if(!isNaN(value)) {
            this._issuedDate = BCMSDate.init().offset(value)
        } else {
            this._issuedDate = value;
        }
    }

    /**
     * @name checkNumber
     * @instance
     * @property set {STRING} - sets the checkNumber
     * @memberof TASPositivePayExceptionCheckForm
     */
    set checkNumber(value) { this._checkNumber = value; }

    /**
     * @name amount
     * @instance
     * @property set {STRING} - sets the amount
     * @memberof TASPositivePayExceptionCheckForm
     */
    set amount(value) { 
        if(value) {
            if(value instanceof BCMSCurrency) {
                this._amount = value;
            } else {
                this._amount = BCMSCurrency.init(value);
            }
        } else {
            this._amount = value;
        }
     }

    /**
     * @name payee
     * @instance
     * @property set {STRING} - sets the payee
     * @memberof TASPositivePayExceptionCheckForm
     */
    set payee(value) { this._payee = value; }

    /**
     * @name referenceNumber
     * @instance
     * @property set {STRING} - sets the referenceNumber
     * @memberof TASPositivePayExceptionCheckForm
     */
    set referenceNumber(value) { this._referenceNumber = value; }

    /**
     * @name type
     * @instance
     * @property set {STRING} - sets the type
     * @memberof TASPositivePayExceptionCheckForm
     */
    set type(value) { this._type = value; }

    /**
     * @name account
     * @instance
     * @property get {STRING} - returns the status.
     * @memberof TASPositivePayExceptionCheckForm
     */
    set status(value) { this._status = value; }

    /**
     * @name reason
     * @instance
     * @property get {STRING} - returns the reason.
     * @memberof TASPositivePayExceptionCheckForm
     */
    set reason(value) { this._reason = value; }

    /**
     * @name action
     * @instance
     * @property get {STRING} - returns the action.
     * @memberof TASPositivePayExceptionCheckForm
     */
    set action(value) { this._action = value; }

    /**
     * @name returnReason
     * @instance
     * @property get {STRING} - returns the returnReason.
     * @memberof TASPositivePayExceptionCheckForm
     */
    set returnReason(value) { this._returnReason = value; }

        /**
     * @name returnReason
     * @instance
     * @property get {STRING} - returns the returnReason.
     * @memberof TASPositivePayExceptionCheckForm
     */
    set didClickReturnReason(value) { this._didClickReturnReason = value; }

    /**
     * @name id
     * @instance
     * @property get {STRING} - returns the id
     * @memberof TASPositivePayExceptionCheckForm
     */
    get id() { return this._id; }

    /**
     * @name account
     * @instance
     * @property get {STRING} - returns the account.
     * @memberof TASPositivePayExceptionCheckForm
     */
    get account() {
        return this._account;
    }

    /**
     * @name issuedDate
     * @instance
     * @property get {STRING} - returns the issuedDate
     * @memberof TASPositivePayExceptionCheckForm
     */
    get issuedDate() { return this._issuedDate; }

    /**
     * @name checkNumber
     * @instance
     * @property get {STRING} - returns the checkNumber
     * @memberof TASPositivePayExceptionCheckForm
     */
    get checkNumber() { return this._checkNumber; }

    /**
     * @name mobileViewIsOpen
     * @instance
     * @property get {STRING} - returns the mobileViewIsOpen
     * @memberof TASPositivePayExceptionCheckForm
     */
    get mobileViewIsOpen() { return this._mobileViewIsOpen; }

    /**
     * @name mobileViewIsEditing
     * @instance
     * @property get {STRING} - returns the mobileViewIsEditing
     * @memberof TASPositivePayExceptionCheckForm
     */
    get mobileViewIsEditing() { return this._mobileViewIsEditing; }

    /**
     * @name amount
     * @instance
     * @property get {STRING} - returns the amount
     * @memberof TASPositivePayExceptionCheckForm
     */
    get amount() { return this._amount; }

    /**
     * @name payee
     * @instance
     * @property get {STRING} - returns the payee
     * @memberof TASPositivePayExceptionCheckForm
     */
    get payee() { return this._payee; }

    /**
     * @name referenceNumber
     * @instance
     * @property get {STRING} - returns the referenceNumber
     * @memberof TASPositivePayExceptionCheckForm
     */
    get referenceNumber() { return this._referenceNumber; }

    /**
     * @name type
     * @instance
     * @property get {STRING} - returns the type
     * @memberof TASPositivePayExceptionCheckForm
     */
    get type() { return this._type; }

    /**
     * @name status
     * @instance
     * @property get {STRING} - returns the status.
     * @memberof TASPositivePayExceptionCheckForm
     */
    get status() { return this._status; }

    /**
     * @name reason
     * @instance
     * @property get {STRING} - returns the reason.
     * @memberof TASPositivePayExceptionCheckForm
     */
    get reason() { return this._reason; }

    /**
     * @name action
     * @instance
     * @property get {STRING} - returns the action.
     * @memberof TASPositivePayExceptionCheckForm
     */
    get action() { return this._action; }

    /**
     * @name returnReason
     * @instance
     * @property get {STRING} - returns the returnReason.
     * @memberof TASPositivePayExceptionCheckForm
     */
    get returnReason() { return this._returnReason; }

    /**
     * @name returnReason
     * @instance
     * @property get {STRING} - returns the returnReason.
     * @memberof TASPositivePayExceptionCheckForm
     */
    get didClickReturnReason() { return this._didClickReturnReason; }
    
    /**
     * @name record
     * @instance
     * @property get {OBJECT} - returns the set values
     * @memberof TASPositivePayExceptionCheckForm
     */
    get record() {
        return {
            id: this.id,
            account: this.account,
            issuedDate: this.issuedDate,
            checkNumber: this.checkNumber,
            amount: this.amount,
            payee: this.payee,
            referenceNumber: this.referenceNumber,
            type: this.type,
            status: this.status,
            reason: this.reason,
            action: this.action,
            returnReason: this.returnReason,
            didClickReturnReason: this.didClickReturnReason
        };
    }

    /**
     * @function reset
     * @instance
     * @property {VOID} - No parameters.
     * @description Returns an instance of the class with default values.
     * @memberof TASPositivePayExceptionCheckForm
     */
    reset() {
        const defaultVals = TASPositivePayExceptionCheckForm.defaultVals();
        return TASPositivePayExceptionCheckForm.init(defaultVals);
    }

    /** 
     * @function defaultVals   
     * @static
     * @property {VOID} - No parameters
     * @description Returns the default values of the class.
     * @memberof TASPositivePayExceptionCheckForm 
     */
    static defaultVals() {
        return { 
            id: "0",
            account: undefined,
            issuedDate: undefined,
            checkNumber: undefined, 
            amount: undefined,
            payee: undefined,
            referenceNumber: undefined,
            type: "issue",
            status: "not reconciled",
            reason: undefined,
            action: undefined,
            returnReason: undefined,
            didClickReturnReason: undefined
        };
    }
    
    /** 
     * @function init   
     * @static
     * @property {OBJECT} - An object to initialize an instance of the class.
     * @description Returns an instance of the class.
     * @memberof TASPositivePayExceptionCheckForm 
     */
    static init(record) {
        const defaultVals = TASPositivePayExceptionCheckForm.defaultVals();
        const vals = { 
            ...defaultVals,
            ...record ?? {} 
        };

        return new TASPositivePayExceptionCheckForm(vals);
    }
}